/**
 * 大会結果画面シーン
 *
 * 最終順位表を表示し、プレイヤーの順位をハイライトする。
 */
import { BaseScene } from './BaseScene';
import { SceneKey } from './sceneKeys';
import { GAME_WIDTH, GAME_HEIGHT } from './gameConfig';
import {
  TournamentState,
  TournamentMode,
  TournamentParticipant,
} from '../types/Tournament';
import { MONSTER_DATABASE } from '../constants/monsters';
import {
  calculateRoundRobinStandings,
  calculateEliminationStandings,
} from '../tournament/standingsCalculator';
import {
  TOURNAMENT_RESULT_LAYOUT as LAYOUT,
  TOURNAMENT_RESULT_LABELS as LABELS,
} from './tournamentResultConfig';
import { BackgroundImageKey } from '../constants/imageKeys';
import { checkTournamentEliminationAchievements, checkTournamentRoundRobinAchievements, unlockAchievements } from '../utils/achievementManager';
import { showAchievementToast } from '../utils/achievementToast';
import { recordTournamentWin, recordAnyTournamentWin, loadGameProgress } from '../utils/gameProgressManager';
import {
  shouldTriggerAliceChallenge,
  shouldShowTournamentFirstWinHint,
  createAliceChallengeBattleData,
} from '../utils/challengeBattleManager';
import { GameMode } from '../types/GameMode';

interface TournamentResultSceneData {
  tournamentState: TournamentState;
}

export class TournamentResultScene extends BaseScene {
  private tournamentState!: TournamentState;
  private pendingAliceChallengeMonsterId?: string;
  private showTournamentHint = false;

  constructor() {
    super(SceneKey.TOURNAMENT_RESULT);
  }

  create(data?: TournamentResultSceneData): void {
    if (this.textures.exists(BackgroundImageKey.TITLE)) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, BackgroundImageKey.TITLE).setOrigin(0.5);
    }

    if (!data?.tournamentState) {
      this.transitionTo(SceneKey.TITLE);
      return;
    }

    this.tournamentState = data.tournamentState;

    const isRoundRobin = this.tournamentState.mode === TournamentMode.ROUND_ROBIN;
    const title = isRoundRobin ? LABELS.roundRobinTitle : LABELS.eliminationTitle;

    const standings = isRoundRobin
      ? calculateRoundRobinStandings(this.tournamentState)
      : calculateEliminationStandings(this.tournamentState);

    const isChampion = standings.findIndex((p) => p.isPlayer) === 0;
    let pendingAliceChallengeMonsterId: string | undefined;
    if (isChampion) {
      const checkFn = isRoundRobin
        ? checkTournamentRoundRobinAchievements
        : checkTournamentEliminationAchievements;
      const newAchievements = unlockAchievements(checkFn(this.tournamentState.aiLevel));
      if (newAchievements.length > 0) showAchievementToast(this, newAchievements);

      // 大会優勝を記録し、アリス挑戦戦のトリガーを確認
      const progressBeforeWin = loadGameProgress();
      this.showTournamentHint = shouldShowTournamentFirstWinHint(progressBeforeWin);
      recordTournamentWin(this.tournamentState.aiLevel);
      recordAnyTournamentWin();
      const updatedProgress = loadGameProgress();
      const playerMonsterId = standings[0]?.monsterId;
      if (shouldTriggerAliceChallenge(updatedProgress) && playerMonsterId) {
        pendingAliceChallengeMonsterId = playerMonsterId;
      }
    }

    this.pendingAliceChallengeMonsterId = pendingAliceChallengeMonsterId;

    this.renderTitle(title);
    this.renderPlayerRank(standings);
    this.renderStandings(standings, isRoundRobin);
    this.renderButtons();
    this.renderAliceChallengeUI();
  }

  private renderTitle(title: string): void {
    this.add
      .text(GAME_WIDTH / 2, LAYOUT.titleY, title, {
        fontSize: '36px',
        color: '#ffffff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private renderPlayerRank(standings: TournamentParticipant[]): void {
    const playerIndex = standings.findIndex((p) => p.isPlayer);
    if (playerIndex < 0) return;

    const rank = playerIndex + 1;
    const isChampion = rank === 1;
    const text = isChampion
      ? LABELS.champion
      : `${LABELS.playerRankPrefix}${rank}位`;
    const color = isChampion ? '#ffdd44' : '#44ff44';
    const fontSize = isChampion ? '32px' : '24px';

    this.add
      .text(GAME_WIDTH / 2, LAYOUT.subtitleY, text, {
        fontSize,
        color,
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private renderStandings(standings: TournamentParticipant[], showPoints: boolean): void {
    const startX = showPoints ? 200 : 250;
    const startY = LAYOUT.tableStartY;
    const rowH = LAYOUT.tableRowHeight;
    const colWidths = showPoints ? [50, 120, 100, 50, 50, 50, 60] : [50, 120, 100];

    // Header
    const headers = showPoints
      ? [LABELS.rank, LABELS.name, LABELS.species, LABELS.win, LABELS.lose, LABELS.draw, LABELS.pts]
      : [LABELS.rank, LABELS.name, LABELS.species];

    let x = startX;
    headers.forEach((h, i) => {
      this.add.text(x, startY, h, {
        fontSize: '16px',
        color: '#ffdd44',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      });
      x += colWidths[i];
    });

    // Rows
    standings.forEach((p, rank) => {
      const y = startY + (rank + 1) * rowH;
      const isPlayer = p.isPlayer;
      const isFirst = rank === 0;
      const color = isFirst ? '#ffdd44' : isPlayer ? '#44ff44' : '#ffffff';
      const monster = MONSTER_DATABASE.find((m) => m.id === p.monsterId);

      x = startX;
      const values = showPoints
        ? [
            `${rank + 1}`,
            monster?.name ?? p.monsterId,
            monster?.species ?? '',
            `${p.wins}`,
            `${p.losses}`,
            `${p.draws}`,
            `${p.points}`,
          ]
        : [
            `${rank + 1}`,
            monster?.name ?? p.monsterId,
            monster?.species ?? '',
          ];

      values.forEach((v, i) => {
        this.add.text(x, y, v, {
          fontSize: '15px',
          color,
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 3,
        });
        x += colWidths[i];
      });
    });
  }

  private renderButtons(): void {
    const buttons = [
      {
        label: LABELS.retry,
        action: () => this.transitionTo(SceneKey.TOURNAMENT_SETUP),
      },
      {
        label: LABELS.toTitle,
        action: () => this.transitionTo(SceneKey.TITLE),
      },
    ];

    buttons.forEach((btn, i) => {
      const y = LAYOUT.buttonStartY + i * LAYOUT.buttonSpacing;
      const text = this.add
        .text(GAME_WIDTH / 2, y, btn.label, {
          fontSize: '24px',
          color: '#ffffff',
          fontFamily: "'Noto Sans JP', Arial, sans-serif",
          stroke: '#000000',
          strokeThickness: 4,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });

      text.on('pointerover', () => { text.setColor('#ffffff'); text.setScale(1.1); });
      text.on('pointerout', () => { text.setColor('#ffffff'); text.setScale(1.0); });
      text.on('pointerdown', btn.action);
    });
  }

  /** アリス挑戦戦UI・大会初優勝ヒントを表示 */
  private renderAliceChallengeUI(): void {
    const cy = LAYOUT.buttonStartY - 80;

    // 大会初優勝ヒント（アリス解放条件未達成時のみ）
    if (this.showTournamentHint && !this.pendingAliceChallengeMonsterId) {
      this.add.text(GAME_WIDTH / 2, cy, '「頂に立ちし導き手よ…さらなる高みを目指せ」', {
        fontSize: '16px',
        color: '#aaaaff',
        fontFamily: "'Noto Sans JP', Arial, sans-serif",
        fontStyle: 'italic',
      }).setOrigin(0.5);
      return;
    }

    if (!this.pendingAliceChallengeMonsterId) return;

    this.showAliceChallengePopup(this.pendingAliceChallengeMonsterId);
  }

  private showAliceChallengePopup(monsterId: string): void {
    const cx = GAME_WIDTH / 2;
    const cy = GAME_HEIGHT / 2;
    const DEPTH = 20;
    const FONT = "'Noto Sans JP', Arial, sans-serif";

    // 半透明オーバーレイ（背後のボタンへの入力を遮断）
    this.add.rectangle(cx, cy, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78)
      .setDepth(DEPTH).setInteractive();

    // パネル
    this.add.rectangle(cx, cy, 580, 300, 0x0a0a1e).setDepth(DEPTH + 1);
    this.add.rectangle(cx, cy, 576, 296, 0x000000, 0)
      .setStrokeStyle(2, 0x8866ff).setDepth(DEPTH + 1);

    // キャラ画像（左寄せ）
    const img = this.add.image(cx - 160, cy, 'monster_alice_battle')
      .setDepth(DEPTH + 2);
    const scale = Math.min(200 / img.width, 240 / img.height);
    img.setScale(scale);

    // テキスト（右エリア）
    this.add.text(cx + 60, cy - 60, '謎の挑戦者が現れた！', {
      fontSize: '20px', color: '#aa88ff', fontFamily: FONT, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DEPTH + 2);

    // 挑戦するボタン
    const btnBg = this.add.rectangle(cx + 60, cy + 50, 190, 46, 0x5533cc)
      .setDepth(DEPTH + 2).setInteractive({ useHandCursor: true });
    this.add.text(cx + 60, cy + 50, '挑戦する！', {
      fontSize: '22px', color: '#ffffff', fontFamily: FONT, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(DEPTH + 3);

    btnBg.on('pointerover', () => btnBg.setFillStyle(0x7755ee));
    btnBg.on('pointerout',  () => btnBg.setFillStyle(0x5533cc));
    btnBg.on('pointerdown', () => {
      const challengeData = createAliceChallengeBattleData(monsterId);
      this.transitionTo(SceneKey.BATTLE, { ...challengeData, mode: GameMode.FREE_CPU });
    });
  }
}
